import { type ComponentPropsWithoutRef, useId } from "react";
import { Check } from "untitledui-js/react";

import { HIDDEN_INPUT, BOX_BASE, INVALID_BOX } from "./customControlStyles";
import { ErrorMessage, type ErrorKey } from "./ErrorMessage";
import { Description } from "./Description";

type CheckboxProps = Omit<ComponentPropsWithoutRef<"input">, "type"> & {
  label: string;
  description?: string;
  error?: ErrorKey;
  indeterminate?: boolean;
};

export const Checkbox = ({
  label,
  description,
  error,
  id,
  required,
  indeterminate,
  "aria-describedby": ariaDescribedBy,
  ...checkboxProps
}: CheckboxProps) => {
  const generatedId = useId();
  const checkboxId = id ?? generatedId;

  const setIndeterminate = (node: HTMLInputElement | null) => {
    if (node) node.indeterminate = Boolean(indeterminate);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="group flex items-center gap-3">
        <span className="relative flex">
          <input
            {...checkboxProps}
            ref={setIndeterminate}
            id={checkboxId}
            type="checkbox"
            required={required}
            aria-describedby={ariaDescribedBy || undefined}
            aria-invalid={error ? "true" : undefined}
            aria-checked={indeterminate ? "mixed" : undefined}
            {...(indeterminate ? { checked: true, readOnly: true } : null)}
            className={HIDDEN_INPUT}
          />
          <span
            aria-hidden
            className={[
              BOX_BASE,
              "mt-px rounded-sm",
              "peer-checked:border-primary-500 peer-checked:bg-primary-500",
              "peer-checked:[&>svg]:scale-100 peer-checked:[&>svg]:opacity-100",
              error ? INVALID_BOX : "",
            ].join(" ")}
          >
            {indeterminate ? (
              <svg width="10" height="2" viewBox="0 0 10 2" aria-hidden>
                <rect width="10" height="2" rx="1" fill="white" />
              </svg>
            ) : (
              <Check
                aria-hidden
                className="size-2.5 scale-50 stroke-white opacity-0 transition duration-150"
              />
            )}
          </span>
        </span>
        <label
          htmlFor={checkboxId}
          className="text-sm leading-5 font-medium group-has-[input:disabled]:cursor-not-allowed group-has-[input:disabled]:text-tertiary-600"
        >
          {label}
        </label>
      </div>

      {error && <ErrorMessage error={error} />}
      {description && !error && <Description description={description} />}
    </div>
  );
};
